/**
 * `IStorage` —— 13 个 store 的唯一契约（迁移并大幅扩展自 `prototype/web/core/storage.js`）。
 *
 * 库名：`meshlink_<did 后 8 位>`，按 DID 分库（ARCH §8.1）。
 * UI 层禁止直接引用实现，只能经 `services/*` 与 `store/*`。
 */

import type {
  ChatEnvelope,
  Contact,
  CachedBlob,
  ContentKeyRecord,
  DayTraffic,
  IdentityMeta,
  Message,
  MsgStatus,
  PendingItem,
  Post,
  Profile,
  PurchaseRecord,
  Report,
} from '@shared/types';
import type { Channel, TxRecord } from '@shared/chain';
import type { EncryptedKeystore } from '@/crypto/keystore';

/** 通用仓储（keyPath 单键）。 */
export interface Repo<T> {
  get(key: IDBValidKey): Promise<T | undefined>;
  put(value: T): Promise<void>;
  putMany(values: T[]): Promise<void>;
  delete(key: IDBValidKey): Promise<void>;
  all(): Promise<T[]>;
  count(): Promise<number>;
  clear(): Promise<void>;
}

/** KV 仓储（settings 用，keyPath = `key`）。 */
export interface KVRepo<V> {
  get(key: string): Promise<V | undefined>;
  set(key: string, value: V): Promise<void>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
  clear(): Promise<void>;
}

/** 消息仓储：按会话倒序分页 + 状态更新 + 未读计数。 */
export interface MessageRepo extends Repo<Message> {
  /** 按会话倒序分页，每页 `limit` 条（默认 30，上拉加载）。 */
  page(peerId: string, beforeTs?: number, limit?: number): Promise<Message[]>;
  byMsgId(msgId: string): Promise<Message | null>;
  updateStatus(msgId: string, status: MsgStatus): Promise<void>;
  countUnread(peerId: string): Promise<number>;
  deleteByPeer(peerId: string): Promise<void>;
}

export interface StorageUsage {
  usageBytes: number;
  quotaBytes: number;
}

export interface IStorage {
  /** 打开按 DID 命名的库。 */
  open(did: string): Promise<void>;
  close(): Promise<void>;
  readonly ready: boolean;
  readonly dbName: string;

  // —— keystore / 身份 ——
  getKeystore(): Promise<EncryptedKeystore | null>;
  putKeystore(keystore: EncryptedKeystore): Promise<void>;
  getIdentityMeta(): Promise<IdentityMeta | null>;
  putIdentityMeta(meta: IdentityMeta): Promise<void>;

  // —— 领域 ——
  contacts: Repo<Contact>;
  messages: MessageRepo;
  pending: Repo<PendingItem>;
  posts: Repo<Post>;
  profiles: Repo<Profile>;
  blobs: Repo<CachedBlob>;
  txs: Repo<TxRecord>;
  channels: Repo<Channel>;
  reports: Repo<Report>;
  traffic: Repo<DayTraffic>;
  settings: KVRepo<unknown>;

  // —— 创作者付费内容（V1）——
  /** 我的已购记录：`contentCid` → 记录（含创作者交付的密钥与明文缓存）。 */
  purchases: KVRepo<PurchaseRecord>;
  /** 我作为创作者的内容密钥：`contentCid` → 记录（用于向已付款买家交付）。 */
  contentKeys: KVRepo<ContentKeyRecord>;

  // —— 运维 ——
  estimateUsage(): Promise<StorageUsage>;
  /** 注销：清库 + 清 keystore。 */
  clearAll(): Promise<void>;
}

/** 库名：`meshlink_<did 后 8 位>`。 */
export function dbNameFor(did: string): string {
  const tail = did.replace(/[^0-9a-zA-Z]/g, '').slice(-8).toLowerCase();
  return `meshlink_${tail || 'default'}`;
}

/** 待发消息队列条目（与 `PendingItem` 对应，envelope 为已签名信封）。 */
export type PendingEnvelope = ChatEnvelope;
